import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { analyticListLimit, analyticListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import FilterTabs from "components/FilterTabs";
import Tooltip from "components/Tooltip";
import EmptyState from "components/EmptyState/EmptyState";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";

interface AnalyticPaymentDepositsItem {
  date: string;
  ftdCount: number;
  ftdSum: number;
  ftdAvg: number;
  redepCount: number;
  redepSum: number;
  redepAvg: number;
  totalDepCount: number;
  totalDepSum: number;
  totalDepAvg: number;
  totalPendingCount: number;
  totalFailedCount: number;
  totalSucceedCount: number;
  conversionRate: number;
  finishRate: number;
}
const AnalyticPaymentDepositsList: React.FC = () => {
  const { t } = useTranslation();

  const [isFetched, setIsFetched] = useState(false);
  const [dateFrom, setDateFrom] = useState(
    moment().subtract(1, "week").toDate(),
  );
  const [dateTo, setDateTo] = useState(new Date());
  const [type, setType] = useState("day");
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(analyticListLimit);
  const [items, setItems] = useState<AnalyticPaymentDepositsItem[]>([]);

  const handleLoadItems = async () => {
    setIsFetched(false);
    try {
      const result = await instance.post(
        `${apiRoutes.paymentAnalyticDeposits}`,
        {
          startDate: moment(dateFrom).format("DD.MM.YYYY"),
          endDate: moment(dateTo).format("DD.MM.YYYY"),
          period: type,
          offset: activePage - 1,
          limit: currentLimit,
        },
      );
      setItems(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
    } catch {
      console.log("handleLoadItems error");
    }
    setIsFetched(true);
  };

  useEffect(() => {
    handleLoadItems();
  }, [dateFrom, dateTo, activePage, type, currentLimit]);

  return (
    <div className="page-list-wrapper analytic-wrapper">
      <div className="page-list-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            <div className="dashboard-filter">
              <div className="row">
                <div className="dashboard-date">
                  <span className="label">{t("from")}:</span>
                  <div className="date-picker-wrapper">
                    <button
                      onClick={() =>
                        setDateFrom(
                          moment(dateFrom).subtract(1, "days").toDate(),
                        )
                      }
                    >
                      &lt;
                    </button>
                    <DatePicker
                      customInput={<ReadOnlyInput />}
                      selected={dateFrom}
                      onChange={(date: any) => setDateFrom(date)}
                      dateFormat={"d/MM/YYYY"}
                    />
                    <button
                      onClick={() =>
                        setDateFrom(moment(dateFrom).add(1, "days").toDate())
                      }
                    >
                      &gt;
                    </button>
                  </div>
                </div>
                <div className="dashboard-date">
                  <span className="label">{t("to")}:</span>
                  <div className="date-picker-wrapper">
                    <button
                      onClick={() =>
                        setDateTo(moment(dateTo).subtract(1, "days").toDate())
                      }
                    >
                      &lt;
                    </button>
                    <DatePicker
                      customInput={<ReadOnlyInput />}
                      selected={dateTo}
                      onChange={(date: any) => setDateTo(date)}
                      dateFormat={"d/MM/YYYY"}
                    />
                    <button
                      onClick={() =>
                        setDateTo(moment(dateTo).add(1, "days").toDate())
                      }
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <FilterTabs
              value={type}
              onSelect={(e) => setType(e as string)}
              options={[
                {
                  value: "hour",
                  label: t("hour"),
                },
                {
                  value: "day",
                  label: t("day"),
                },
                {
                  value: "week",
                  label: t("week"),
                },
                {
                  value: "month",
                  label: t("month"),
                },
                {
                  value: "year",
                  label: t("year"),
                },
              ]}
            />
          </div>
        </div>

        {!isFetched && (
          <>
            <DataTableSkeleton columnsCount={18} rowsCount={8} />
            <PaginationSkeleton />
          </>
        )}

        {isFetched && items.length === 0 && (
          <div className="game-table-wrap table">
            <EmptyState message={t("noDepositDataFound")} />
          </div>
        )}

        {isFetched && items.length > 0 && (
          <>
            <div className="game-table-wrap table">
              <table className="game-table">
                <thead>
                  <tr>
                    <th className="separator main">
                      <div className="th-content">
                        {t("date")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("ftd")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("ftdSum")}
                        <span className="thead-label">sum</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th className="separator">
                      <div className="th-content">
                        {t("ftdAvg")}
                        <span className="thead-label">avg</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("reDep")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("reDepSum")}
                        <span className="thead-label">sum</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th className="separator">
                      <div className="th-content">
                        {t("reDepAvg")}
                        <span className="thead-label">avg</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("totalDep")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("totalDepSum")}
                        <span className="thead-label">sum</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("totalDepAvg")}
                        <span className="thead-label">avg</span>
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("pending")} #
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("failed")} #
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("succeed")} #
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("conversion")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>

                    <th>
                      <div className="th-content">
                        {t("finishRate")}
                        <Tooltip text="Info" position="right" />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((el, idx) => (
                    <tr key={`${el.date}-${idx}`}>
                      <td className="separator main">
                        {moment
                          .utc(el.date)
                          .format(
                            type === "hour" ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY",
                          )}
                      </td>
                      <td>{el.ftdCount}</td>
                      <td>{el.ftdSum}</td>
                      <td className="separator">{el.ftdAvg}</td>
                      <td>{el.redepCount}</td>
                      <td>{el.redepSum}</td>
                      <td className="separator">{el.redepAvg}</td>
                      <td>{el.totalDepCount}</td>
                      <td>{el.totalDepSum}</td>
                      <td>{el.totalDepAvg}</td>
                      <td>{el.totalPendingCount}</td>
                      <td>{el.totalFailedCount}</td>
                      <td>{el.totalSucceedCount}</td>
                      <td>{el.conversionRate}</td>
                      <td>{el.finishRate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              activePage={activePage}
              limit={currentLimit}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={analyticListLimits}
              handleLimitClick={(limit) => {
                setCurrentLimit(limit);
                setActivePage(1);
              }}
              handleNextClick={() => {
                if (activePage !== totalPages) setActivePage(activePage + 1);
              }}
              handlePrevClick={() => {
                if (activePage !== 1) setActivePage(activePage - 1);
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticPaymentDepositsList;
